import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { addCategory, getCategories, updateCategory, deleteCategory } from '../firebase/services';
import BilingualInput from '../Components/BilingualInput';
import { resizeImage } from '../Utils/imageHelpers';
import './Admin.css';

export default function ManageCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    nameAr: '',
    imageUrl: '',
    customLink: '',
    parentId: '',
    tags: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  // const handleNameChange = (fieldName, value) => {
  //   setFormData(prev => {
  //     const newData = { ...prev, [fieldName]: value };
  //     // Auto-generate link from English name if available
  //     const nameEn = fieldName === 'nameEn' ? value : newData.nameEn;
  //     if (!isLinkManuallyEdited && !editingCategory && !prev.parentId && nameEn) {
  //       const slug = nameEn.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  //       newData.customLink = slug ? `/shop/${slug}` : '';
  //     }
  //     return newData;
  //   });
  // };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleLinkChange = (e) => {
    setFormData({ ...formData, customLink: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const combinedName = [formData.nameEn, formData.nameAr].filter(Boolean).join(' / ');
      if (!combinedName) {
        toast.error('Category name (EN or AR) is required');
        setLoading(false);
        return;
      }

      // Check for duplicates
      const slug = formData.nameEn.toLowerCase().trim();
      const parentId = formData.parentId || null;
      
      const duplicate = categories.find(c => {
        const isSameName = c.nameEn && c.nameEn.toLowerCase().trim() === slug;
        const isSameParent = (c.parentId || null) === parentId;
        const isNotSelf = c.id !== (editingCategory ? editingCategory.id : null);
        return isSameName && isSameParent && isNotSelf;
      });
      
      if (duplicate) {
        toast.error(`Category already exists in this level!`);
        setLoading(false);
        return;
      }

      if (!formData.parentId && !imageFile && !formData.imageUrl) {
        toast.error('Please select an image');
        setLoading(false);
        return;
      }

      const categoryData = {
        name: combinedName,
        nameEn: formData.nameEn,
        nameAr: formData.nameAr,
        imageUrl: formData.imageUrl,
        customLink: formData.customLink,
        parentId: formData.parentId || null,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryData, imageFile);
        toast.success('Category updated successfully');
      } else {
        await addCategory(categoryData, imageFile);
        toast.success('Category added successfully');
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to save category');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    // Parse EN/AR from combined name if separate fields don't exist
    const nameEn = category.nameEn ?? (typeof category.name === 'string' ? category.name.split(' / ')[0].trim() : '');
    const nameAr = category.nameAr ?? (typeof category.name === 'string' && category.name.includes(' / ') ? category.name.split(' / ')[1].trim() : '');

    setEditingCategory(category);
    setFormData({
      name: category.name,
      nameEn,
      nameAr,
      imageUrl: category.imageUrl,
      customLink: category.customLink || '',
      parentId: category.parentId || '',
      tags: category.tags ? category.tags.join(', ') : ''
    });
    setImagePreview(category.imageUrl);
    // setIsLinkManuallyEdited(true); // Don't auto-update link when editing existing category
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(categoryId);
        toast.success('Category deleted successfully');
        fetchCategories();
      } catch (error) {
        toast.error('Failed to delete category');
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', nameEn: '', nameAr: '', imageUrl: '', customLink: '', parentId: '' });
    setEditingCategory(null);
    setImageFile(null);
    setImagePreview(null);
    // setIsLinkManuallyEdited(false);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const resizedFile = await resizeImage(file, 800, 800);
        setImageFile(resizedFile);
        const previewUrl = URL.createObjectURL(resizedFile);
        setImagePreview(previewUrl);
        setFormData({ ...formData, imageUrl: previewUrl });
      } catch (error) {
        console.error("Error resizing image:", error);
        toast.error("Failed to process image");
      }
    }
  };



  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Manage Categories</h2>
        <p>Create, edit, and organize your store categories.</p>
      </div>
      
      <div className="form-grid">
        {/* Left Column: Form */}
        <div>
          <div className="admin-card">
            <div className="section-title">
              <ion-icon name={editingCategory ? "create-outline" : "add-circle-outline"}></ion-icon>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </div>
            
            <form onSubmit={handleSubmit}>
              <BilingualInput
                label="Category Name"
                fieldNameEn="nameEn"
                fieldNameAr="nameAr"
                valueEn={formData.nameEn}
                valueAr={formData.nameAr}
                onChange={handleChange}
                placeholder="Enter category name"
                required
              />

              <div className="input-group">
                <label>Parent Category (Optional)</label>
                <select
                  className="custom-select"
                  value={formData.parentId}
                  onChange={(e) => {
                    const pid = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      parentId: pid,
                      customLink: pid ? '' : prev.customLink,
                      imageUrl: pid ? '' : prev.imageUrl
                    }));
                    if (pid) {
                      setImageFile(null);
                      setImagePreview(null);
                    }
                  }}
                >
                  <option value="">None (Top Level)</option>
                  {categories
                    .filter(c => !c.parentId && c.id !== editingCategory?.id) // Only show top-level categories
                    .map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))
                  }
                </select>
                <small className="helper-text">
                  Select a parent to make this a sub-category. Only top-level categories can be parents.
                </small>
              </div>

              {!formData.parentId && (
                <>
                  <div className="input-group">
                    <label>Custom Link (Auto-generated)</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={formData.customLink}
                      onChange={handleLinkChange}
                      placeholder="/shop/food"
                    />
                    <small className="helper-text">
                      Automatically generated from name. You can edit it manually.
                    </small>
                  </div>

                  <div className="input-group">
                    <label>Tags (Comma separated)</label>
                    <input
                      type="text"
                      className="custom-input"
                      value={formData.tags}
                      onChange={(e) => handleChange('tags', e.target.value)}
                      placeholder="e.g. Summer, Sale, New Arrival"
                    />
                    <small className="helper-text">
                      Used for "You may like" recommendations.
                    </small>
                  </div>

                  <div className="input-group">
                    <label>Category Image</label>
                    <label htmlFor="cat-image-upload" className="image-upload-area">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="image-preview" />
                      ) : (
                        <>
                          <div className="upload-icon">
                            <ion-icon name="cloud-upload-outline"></ion-icon>
                          </div>
                          <p>Click to upload image</p>
                        </>
                      )}
                    </label>
                    <input
                      id="cat-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{display: 'none'}}
                      required={!editingCategory && !formData.imageUrl}
                    />
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading} style={{flex: 1}}>
                  {loading ? 'Saving...' : (editingCategory ? 'Update Category' : 'Add Category')}
                </button>
                {editingCategory && (
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: List */}
        <div>
          <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
            <div className="card-header">
              <h3>Existing Categories</h3>
            </div>
            
            {loading && categories.length === 0 ? (
              <div style={{padding: '40px', textAlign: 'center', color: '#6b7280'}}>
                Loading...
              </div>
            ) : (
              <div className="categories-list" style={{padding: '20px'}}>
                {(() => {
                  const parents = categories.filter(c => !c.parentId);
                  const children = categories.filter(c => c.parentId);
                  
                  // Handle orphans (children with non-existent parents)
                  const parentIds = parents.map(p => p.id);
                  const orphans = children.filter(c => !parentIds.includes(c.parentId));

                  if (parents.length === 0 && orphans.length === 0) {
                     return <div style={{textAlign: 'center', color: '#6b7280'}}>No categories found. Add your first one!</div>;
                  }

                  return (
                    <>
                      {parents.map(parent => {
                        const myChildren = children.filter(c => c.parentId === parent.id);
                        
                        return (
                          <div key={parent.id} className="category-item-parent" style={{marginBottom: '15px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden'}}>
                            {/* Parent Header */}
                            <div style={{display: 'flex', alignItems: 'center', padding: '15px', background: '#fff', borderBottom: myChildren.length > 0 ? '1px solid #f3f4f6' : 'none'}}>
                               <div style={{marginRight: '15px'}}>
                                  {parent.imageUrl ? (
                                    <img src={parent.imageUrl} alt={parent.name} style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px'}} />
                                  ) : (
                                    <div style={{width: '50px', height: '50px', background: '#f3f4f6', borderRadius: '6px'}}></div>
                                  )}
                               </div>
                               <div style={{flex: 1}}>
                                  <h4 style={{margin: 0, fontSize: '1rem', color: '#111827', fontWeight: 600}}>{parent.name}</h4>
                                  <span style={{fontSize: '0.8rem', color: '#6b7280'}}>{parent.customLink || 'Default Link'}</span>
                               </div>
                               <div style={{display: 'flex', gap: '8px'}}>
                                  <button className="action-btn edit" onClick={() => handleEdit(parent)} title="Edit"><ion-icon name="create-outline"></ion-icon></button>
                                  <button className="action-btn delete" onClick={() => handleDelete(parent.id)} title="Delete"><ion-icon name="trash-outline"></ion-icon></button>
                               </div>
                            </div>

                            {/* Children List */}
                            {myChildren.length > 0 && (
                              <div style={{background: '#f9fafb', padding: '10px 15px'}}>
                                <h5 style={{margin: '0 0 10px 0', fontSize: '0.75rem', textTransform: 'uppercase', color: '#9ca3af', letterSpacing: '0.5px', fontWeight: 600}}>Sub Categories</h5>
                                <div style={{display: 'grid', gap: '8px'}}>
                                  {myChildren.map(child => (
                                    <div key={child.id} style={{display: 'flex', alignItems: 'center', background: '#fff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e5e7eb'}}>
                                       <div style={{marginRight: '10px'}}>
                                          {child.imageUrl ? (
                                            <img src={child.imageUrl} alt={child.name} style={{width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px'}} />
                                          ) : (
                                            <div style={{width: '30px', height: '30px', background: '#f3f4f6', borderRadius: '4px'}}></div>
                                          )}
                                       </div>
                                       <div style={{flex: 1, fontSize: '0.9rem', color: '#374151'}}>
                                          {child.name}
                                       </div>
                                       <div style={{display: 'flex', gap: '5px'}}>
                                          <button className="action-btn edit" onClick={() => handleEdit(child)} style={{padding: '4px'}}><ion-icon name="create-outline"></ion-icon></button>
                                          <button className="action-btn delete" onClick={() => handleDelete(child.id)} style={{padding: '4px'}}><ion-icon name="trash-outline"></ion-icon></button>
                                       </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {orphans.length > 0 && (
                        <div style={{marginTop: '20px', borderTop: '2px dashed #e5e7eb', paddingTop: '20px'}}>
                           <h4 style={{color: '#ef4444', marginBottom: '10px'}}>Orphaned Categories (Parent Deleted)</h4>
                           {orphans.map(orphan => (
                              <div key={orphan.id} className="category-item-parent" style={{marginBottom: '10px', border: '1px solid #fca5a5', borderRadius: '8px', background: '#fef2f2', padding: '10px'}}>
                                 <div style={{display: 'flex', alignItems: 'center'}}>
                                    <div style={{flex: 1, color: '#991b1b'}}>{orphan.name}</div>
                                    <div style={{display: 'flex', gap: '8px'}}>
                                      <button className="action-btn edit" onClick={() => handleEdit(orphan)}><ion-icon name="create-outline"></ion-icon></button>
                                      <button className="action-btn delete" onClick={() => handleDelete(orphan.id)}><ion-icon name="trash-outline"></ion-icon></button>
                                   </div>
                                 </div>
                              </div>
                           ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
